import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck, ShieldOff, Smartphone } from "lucide-react";

function Field({ label, type = "password", value, onChange, testId, placeholder, autoComplete }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-slate-500">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        placeholder={placeholder} autoComplete={autoComplete}
        className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900" />
    </label>
  );
}

export default function AccountPage() {
  const { user, setUser } = useAuth();

  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [twofa, setTwofa] = useState(!!user?.twofa_enabled);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [disablePw, setDisablePw] = useState("");

  const changePw = async (e) => {
    e.preventDefault();
    if (nw.length < 6) { toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères"); return; }
    if (nw !== cf) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Mot de passe modifié avec succès");
      setCur(""); setNw(""); setCf("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSavingPw(false);
  };

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data); setCode("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Double authentification activée");
      setTwofa(true); setSetup(null); setCode("");
      setUser((u) => (u ? { ...u, twofa_enabled: true } : u));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePw });
      toast.success("Double authentification désactivée");
      setTwofa(false); setShowDisable(false); setDisablePw("");
      setUser((u) => (u ? { ...u, twofa_enabled: false } : u));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 pb-10" data-testid="account-page">
      <div>
        <h1 className="font-head text-2xl font-bold text-slate-900 dark:text-white">Mon compte</h1>
        <p className="mt-1 text-sm text-slate-500">{user?.name} · {user?.email}</p>
      </div>

      {/* Change password */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-slate-900 dark:text-white"><KeyRound className="h-5 w-5 text-amber-500" /> Changer mon mot de passe</h2>
        <form onSubmit={changePw} className="mt-4 space-y-4">
          <Field label="Mot de passe actuel" value={cur} onChange={setCur} testId="acc-current-pw" autoComplete="current-password" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nouveau mot de passe" value={nw} onChange={setNw} testId="acc-new-pw" autoComplete="new-password" />
            <Field label="Confirmer le nouveau" value={cf} onChange={setCf} testId="acc-confirm-pw" autoComplete="new-password" />
          </div>
          <Button type="submit" disabled={savingPw || !cur || !nw || !cf} data-testid="acc-change-pw-btn"
            className="h-11 bg-slate-900 text-white hover:bg-slate-800 dark:bg-amber-500 dark:text-slate-900 dark:hover:bg-amber-400">
            {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Modifier le mot de passe"}
          </Button>
        </form>
      </section>

      {/* 2FA */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-head text-lg font-bold text-slate-900 dark:text-white"><ShieldCheck className="h-5 w-5 text-amber-500" /> Double authentification</h2>
          <span data-testid="acc-2fa-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${twofa ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}`}>
            {twofa ? "Activée" : "Désactivée"}
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-500">Protégez votre compte avec un code à 6 chiffres généré par une application (Google Authenticator, Authy, Microsoft Authenticator…).</p>

        {!twofa && !setup && (
          <Button onClick={startSetup} disabled={busy} data-testid="acc-2fa-start-btn" className="mt-4 h-11 bg-amber-500 text-slate-900 hover:bg-amber-400">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Smartphone className="mr-2 h-4 w-4" /> Activer la double authentification</>}
          </Button>
        )}

        {!twofa && setup && (
          <div className="mt-4 space-y-4" data-testid="acc-2fa-setup">
            <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-300">
              <li>Ouvrez votre application d'authentification.</li>
              <li>Scannez ce QR code (ou saisissez la clé manuelle).</li>
              <li>Entrez le code à 6 chiffres affiché pour valider.</li>
            </ol>
            <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
              <img src={setup.qr_data_url} alt="QR 2FA" className="h-44 w-44 rounded-lg bg-white p-2" data-testid="acc-2fa-qr" />
              <div className="text-center">
                <p className="text-xs text-slate-400">Clé manuelle</p>
                <code className="select-all break-all text-sm font-semibold text-slate-700 dark:text-slate-200">{setup.secret}</code>
              </div>
            </div>
            <input type="text" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
              data-testid="acc-2fa-code" placeholder="123456" inputMode="numeric"
              className="h-12 w-full rounded-lg border border-slate-300 bg-white px-3 text-center text-xl font-bold tracking-[0.3em] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900" />
            <div className="flex gap-2">
              <Button onClick={enable} disabled={busy || code.length < 6} data-testid="acc-2fa-enable-btn" className="h-11 flex-1 bg-emerald-600 text-white hover:bg-emerald-500">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Vérifier et activer"}
              </Button>
              <Button onClick={() => setSetup(null)} variant="ghost" data-testid="acc-2fa-cancel-btn" className="h-11">Annuler</Button>
            </div>
          </div>
        )}

        {twofa && !showDisable && (
          <Button onClick={() => setShowDisable(true)} variant="outline" data-testid="acc-2fa-disable-start-btn" className="mt-4 h-11 border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400">
            <ShieldOff className="mr-2 h-4 w-4" /> Désactiver la double authentification
          </Button>
        )}

        {twofa && showDisable && (
          <div className="mt-4 space-y-3" data-testid="acc-2fa-disable">
            <Field label="Confirmez votre mot de passe pour désactiver" value={disablePw} onChange={setDisablePw} testId="acc-2fa-disable-pw" autoComplete="current-password" />
            <div className="flex gap-2">
              <Button onClick={disable} disabled={busy || !disablePw} data-testid="acc-2fa-disable-confirm-btn" className="h-11 bg-red-600 text-white hover:bg-red-500">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer la désactivation"}
              </Button>
              <Button onClick={() => { setShowDisable(false); setDisablePw(""); }} variant="ghost" data-testid="acc-2fa-disable-cancel-btn" className="h-11">Annuler</Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
